// Shared wire protocol between the Publisher parent (apps/publisher) and the
// nav bridge running inside the sandboxed preview iframe. Kept dependency-free
// so both sides — and this module's own unit tests — can import it without a
// DOM or Next.js runtime.

export type PublisherNavStartMessage = {
  type: "publisher:nav-start";
  path: string;
};
export type PublisherNavMessage = { type: "publisher:nav"; path: string };
export type PublisherNavDoneMessage = {
  type: "publisher:nav-done";
  path: string;
};
export type PublisherNavAckMessage = { type: "publisher:nav-ack" };
export type PublisherPageUnavailableMessage = {
  type: "publisher:page-unavailable";
  /** Absolute URL the swallowed click was headed to, so the parent's modal can
   *  name the destination instead of describing it in the abstract. */
  url: string;
};
export type PublisherScrollMessage = { type: "publisher:scroll"; y: number };
/** Sent on every highlight-island mount, including hot-reload remounts that
 *  produce no navigation. The parent replies with the current snippet set. */
export type PublisherHighlightReadyMessage = {
  type: "publisher:highlight-ready";
};
/** One outlined element. Every place a global copy change reached renders the
 *  same words, so the locator is what lets a tray row target this element
 *  rather than its identical siblings. */
export type SnippetInstance = {
  snippet: string;
  /** Where the place sits in the document, as a selector — both the identity of
   *  the place and the way a row reveals it. The bridge builds it from the
   *  smallest enclosing block that carries text of its own, not from the node
   *  that matched, so a cycling hero rebuilding its inner slide leaves it
   *  unchanged and two captures of one place agree exactly. */
  locator: string;
  /** What this place is, in the words the user would use: "Main headline". */
  label: string;
};
/** Sent after every apply, enumerating what is outlined right now. The parent
 *  counts commits, so this is the only account of the separate places on the
 *  page a single commit reached. */
export type PublisherHighlightAppliedMessage = {
  type: "publisher:highlight-applied";
  instances: SnippetInstance[];
};
/** The user touched the preview, so the parent drops any hidden-only reveal it
 *  would otherwise send or replay on the next remount. */
export type PublisherRevealCancelledMessage = {
  type: "publisher:reveal-cancelled";
};

export type PublisherOutboundMessage =
  | PublisherNavStartMessage
  | PublisherNavMessage
  | PublisherNavDoneMessage
  | PublisherNavAckMessage
  | PublisherPageUnavailableMessage
  | PublisherScrollMessage
  | PublisherHighlightReadyMessage
  | PublisherHighlightAppliedMessage
  | PublisherRevealCancelledMessage
  | { type: "publisher:asset-ready" }
  | {
      type: "publisher:asset-select";
      path: string;
      src: string;
      label?: string;
    }
  | { type: "publisher:asset-undo"; path: string; src: string };

export type PublisherNavigateMessage = {
  type: "publisher:navigate";
  path: string;
};
export type PublisherReloadMessage = { type: "publisher:reload" };
export type PublisherScrollToMessage = {
  type: "publisher:scroll-to";
  y: number;
};
export type PublisherHighlightMessage = {
  type: "publisher:highlight";
  /** Replaces the marked set wholesale. An empty array clears it. */
  snippets: string[];
  /** Whether the marks are painted. Matching and the instance report run
   *  either way, so hiding the outline never changes what the tray lists.
   *  Absent means painted. */
  visible?: boolean;
};
export type PublisherRevealMessage = {
  type: "publisher:reveal";
  /** Tried in order; the bridge reveals the first that matches. */
  snippets: string[];
  /** The exact element to reveal. Omitted means the first match, which is what
   *  a change with a single place on the page wants. */
  locator?: string;
  /** Reveal only a match inside a closed disclosure, retried as the page
   *  re-renders until the user interacts; copy already on screen is left alone.
   *  Empty snippets drop a pending one. */
  hiddenOnly?: boolean;
};
export type PublisherShimmerMessage = {
  type: "publisher:shimmer";
  /** Old copy of edits still in flight; the shimmer plays over these until
   *  the edit lands. An empty array clears it. */
  snippets: string[];
};
export type PublisherBuildingMessage = {
  type: "publisher:building";
  /** True while the agent is building a section: the bridge shows a placeholder
   *  shimmer where that section will render. False clears it. Carries no
   *  model-controlled text — the box is shimmer only. */
  active: boolean;
};

export type PublisherInboundMessage =
  | PublisherNavigateMessage
  | PublisherReloadMessage
  | PublisherScrollToMessage
  | PublisherHighlightMessage
  | PublisherRevealMessage
  | PublisherShimmerMessage
  | PublisherBuildingMessage
  | { type: "publisher:asset-enable"; enabled: boolean; sanity?: boolean }
  | { type: "publisher:asset-shimmer"; srcs: string[] }
  | { type: "publisher:asset-reveal"; src: string }
  | { type: "publisher:asset-undoable"; srcs: string[]; ready?: boolean }
  | { type: "publisher:asset-error"; srcs: string[] }
  | { type: "publisher:asset-highlight"; srcs: string[] }
  | {
      type: "publisher:asset-preview";
      swaps: { from: string; to: string }[];
    };

/**
 * The security boundary for `publisher:navigate`: only a site-relative path is
 * safe to hand to `router.push`. A leading "/" that is not itself followed by a
 * second "/" (or a "\", which URL parsers normalise to "/") pins the reference
 * to our own origin, which rules out absolute and protocol-relative URLs
 * regardless of what follows. Control characters are rejected separately
 * because browsers strip them before parsing, so "/\t/evil.com" would otherwise
 * smuggle a protocol-relative URL past the prefix check.
 *
 * Deliberately permissive after the prefix: ":" and "\" are legitimate deeper
 * in a path or query (`?next=https://…`), and rejecting them there would drop
 * soft navigation back to a full iframe remount for no security gain.
 */
export function isSafeNavPath(value: unknown): value is string {
  if (typeof value !== "string" || value.length === 0) return false;
  if (value.charAt(0) !== "/") return false;
  if (value.charAt(1) === "/" || value.charAt(1) === "\\") return false;
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    if (code < 0x20 || code === 0x7f) return false;
  }
  return true;
}

export function isSafeScrollY(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0;
}

// Scheme-checked because the parent renders this href as text: a javascript:
// or data: URL is never followed, but printing one invites a copy-paste that would.
export const MAX_DESTINATION_URL_LENGTH = 2048;

export function isSafeDestinationUrl(value: unknown): value is string {
  if (typeof value !== "string") return false;
  if (value.length === 0 || value.length > MAX_DESTINATION_URL_LENGTH)
    return false;
  return value.startsWith("http://") || value.startsWith("https://");
}

export const ASSET_URL_PREFIX =
  "https://cdn.sanity.io/images/4bgok82s/monorepo-static-assets/";

const ASSET_URL_RE =
  /^https:\/\/cdn\.sanity\.io\/images\/4bgok82s\/monorepo-static-assets\/[a-f0-9]{40}-\d{1,5}x\d{1,5}\.(?:webp|gif|png|jpe?g|avif)$/;

export function isAssetImageUrl(value: unknown): value is string {
  return typeof value === "string" && ASSET_URL_RE.test(value);
}

// The resolver puts this value inside single quotes in a `grep` command, so the
// character set is the security boundary, not just validation.
const IMAGE_REF_RE =
  /^(?:https:\/\/[A-Za-z0-9.-]+)?\/[A-Za-z0-9._~%/+,=&?@:-]*$/;
const MAX_IMAGE_REF = 2048;

export function isImageRef(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.length <= MAX_IMAGE_REF &&
    IMAGE_REF_RE.test(value) &&
    !value.startsWith("/_next/image")
  );
}

// Transparent stand-in so an empty slot has an <img> to replace; `?slot=` keeps each use unique.
export const EMPTY_MEDIA_SLOT_SRC =
  "https://cdn.sanity.io/images/4bgok82s/monorepo-static-assets/324a0323af79f3142387d4761198f9ace2d78b3d-1x1.webp";

export function isEmptyMediaSlot(src: string): boolean {
  return (
    src === EMPTY_MEDIA_SLOT_SRC || src.startsWith(`${EMPTY_MEDIA_SLOT_SRC}?`)
  );
}

const SANITY_DOCUMENT_IMAGE_RE =
  /^https:\/\/cdn\.sanity\.io\/images\/4bgok82s\/(?:production|development)\//;

export function isSanityDocumentImage(src: string): boolean {
  return SANITY_DOCUMENT_IMAGE_RE.test(src);
}

// Mirrors the /preview/<label>.<exp>.<sig> basePath minted in apps/publisher's
// preview-token.ts. Next prefixes it to local image paths only.
const PREVIEW_BASE_PATH_RE =
  /^\/preview\/[a-z0-9-]{1,63}\.\d+\.[0-9a-f]{16}(?=\/)/;

export function stripPreviewBasePath(src: string): string {
  return src.replace(PREVIEW_BASE_PATH_RE, "");
}

// fin-ai's default loader and intercom-com's custom images.path.
const IMAGE_OPTIMIZER_PATHS = ["/_next/image", "/ims-image"];

// An optimized img src points at the optimizer; the asset in code is its url=.
export function unwrapOptimizedImage(src: string): string {
  const query = src.indexOf("?");
  if (query === -1 || !IMAGE_OPTIMIZER_PATHS.includes(src.slice(0, query))) {
    return src;
  }
  return new URLSearchParams(src.slice(query + 1)).get("url") ?? src;
}

export function stripBasePath(path: string, basePath: string | null): string {
  if (!basePath || basePath === "/") return path;
  if (path === basePath) return "/";
  if (
    path.startsWith(`${basePath}/`) ||
    path.startsWith(`${basePath}?`) ||
    path.startsWith(`${basePath}#`)
  ) {
    return path.slice(basePath.length);
  }
  return path;
}

// Snippets are model-controlled but only ever compared against textContent,
// so there's no injection surface — these bounds just stop a pathological
// payload from stalling the tree walk (rejected wholesale, not truncated).
export const MAX_SNIPPETS = 200;
export const MAX_SNIPPET_LENGTH = 500;

export function isSafeSnippet(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.length > 0 &&
    value.length <= MAX_SNIPPET_LENGTH
  );
}

export function isSafeSnippets(value: unknown): value is string[] {
  if (!Array.isArray(value)) return false;
  if (value.length > MAX_SNIPPETS) return false;
  return value.every(isSafeSnippet);
}

// One outlined element per instance. The bridge truncates its own report to
// this, so the parent's check here only ever catches a malformed message.
export const MAX_INSTANCES = 4000;
// A label is page text, rendered into a tray row — bounded so a heading-less
// page or a pathological one can't push an essay through the row.
export const MAX_LABEL_LENGTH = 80;

// A locator is built by the bridge from tag names and child positions only, and
// is fed straight back into querySelector, so it is validated on both sides of
// the boundary against exactly the shape the bridge can produce — nothing here
// can carry a selector the preview did not author.
// Deep enough for a real marketing page: locatorFor walks from body, and a
// heading inside a nested section routinely spends 300+ characters getting there.
export const MAX_LOCATOR_LENGTH = 1200;
const LOCATOR =
  /^[a-z][a-z0-9-]*(:nth-child\(\d{1,4}\))?( > [a-z][a-z0-9-]*(:nth-child\(\d{1,4}\))?)*$/;

export function isSafeLocator(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.length > 0 &&
    value.length <= MAX_LOCATOR_LENGTH &&
    LOCATOR.test(value)
  );
}

// Instances come back from the sandboxed preview, so they are untrusted input
// on the parent's side of the boundary in exactly the way snippets are going
// out.
export function isSafeSnippetInstance(
  value: unknown,
): value is SnippetInstance {
  if (!value || typeof value !== "object") return false;
  const { snippet, locator, label } = value as Partial<SnippetInstance>;
  return (
    isSafeSnippet(snippet) &&
    isSafeLocator(locator) &&
    typeof label === "string" &&
    label.length <= MAX_LABEL_LENGTH
  );
}

/** The instances worth trusting, or null if the message itself is malformed.
 *  Filtered rather than rejected wholesale: one unusable entry — an over-deep
 *  locator, say — would otherwise cost the tray every row on the page. */
export function safeSnippetInstances(value: unknown): SnippetInstance[] | null {
  if (!Array.isArray(value)) return null;
  if (value.length > MAX_INSTANCES) return null;
  return value.filter(isSafeSnippetInstance);
}
